import * as React from "react"

// useState that survives reloads and navigation, stored per browser in
// localStorage. Reads through useSyncExternalStore so the server render and
// first client render use `initial` (no hydration mismatch), then switch to the
// stored value. Same-tab and cross-tab updates both propagate.

const EVENT = "persisted-state"

function subscribe(cb: () => void) {
  window.addEventListener("storage", cb)
  window.addEventListener(EVENT, cb)
  return () => {
    window.removeEventListener("storage", cb)
    window.removeEventListener(EVENT, cb)
  }
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

export function usePersistedState<T>(
  key: string,
  initial: T,
  /** Reject stored values that no longer fit (e.g. a removed layout). */
  isValid?: (value: unknown) => value is T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const storageKey = `crm:${key}`
  const raw = React.useSyncExternalStore(
    subscribe,
    () => read(storageKey),
    () => null,
  )

  const value = React.useMemo<T>(() => {
    if (raw === null) return initial
    try {
      const parsed = JSON.parse(raw) as unknown
      return !isValid || isValid(parsed) ? (parsed as T) : initial
    } catch {
      return initial
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw])

  const set = React.useCallback(
    (next: T | ((prev: T) => T)) => {
      let resolved: T
      if (typeof next === "function") {
        // Resolve against the latest stored value so rapid updates compose.
        const stored = read(storageKey)
        let prev = initial
        try {
          if (stored !== null) {
            const parsed = JSON.parse(stored) as unknown
            if (!isValid || isValid(parsed)) prev = parsed as T
          }
        } catch {}
        resolved = (next as (prev: T) => T)(prev)
      } else {
        resolved = next
      }
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(resolved))
      } catch {
        // Storage blocked or full: the change just won't persist.
      }
      window.dispatchEvent(new Event(EVENT))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey],
  )

  return [value, set]
}
